import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  formatDateTime,
  getErrorMessage,
  DateRangeFilterPopover,
  MultiSelectFilterPopover,
  TextFilterPopover,
  type SortDir,
} from '../../../../shared/src';
import {
  api,
  FAKTO_SUPPORT_FILTER,
  NO_PROJECT_FILTER,
  type AuditLogEntry,
  type AuditLogFilterOptions,
  type AuditLogFilters,
  type AuditLogSortColumn,
} from '../../api';
import { FAKTO_SUPPORT_LABEL, eventLabel, projectLabel, summarize } from '../../lib/auditEvents';
import { AuditActor } from './AuditActor';
import { AuditLogDetailsPanel } from './AuditLogDetailsPanel';
import { ExportAuditLogsModal } from '../modals';
import './AuditLogsTab.css';

/** Rows per page on offer; the app-api serves up to 200. */
const PAGE_SIZES = [20, 50, 100, 200];

/**
 * created_at is rendered in the browser's time zone, and the date filter is
 * read in the same zone.
 */
const VIEWER_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface Filters {
  events: string[];
  user_ids: string[];
  project_ids: string[];
  search: string | null;
  exclude_fakto: boolean;
  date_from: string | null;
  date_to: string | null;
}

const defaultFilters: Filters = {
  events: [],
  user_ids: [],
  project_ids: [],
  search: null,
  exclude_fakto: false,
  date_from: null,
  date_to: null,
};

/** The filters as the API takes them, empty ones left out. */
function toApiFilters(filters: Filters): AuditLogFilters {
  return {
    ...(filters.events.length > 0 && { events: filters.events }),
    ...(filters.user_ids.length > 0 && { user_ids: filters.user_ids }),
    ...(filters.project_ids.length > 0 && { project_ids: filters.project_ids }),
    ...(filters.search && { search: filters.search }),
    ...(filters.exclude_fakto && { exclude_fakto: true }),
    ...(filters.date_from && { date_from: filters.date_from }),
    ...(filters.date_to && { date_to: filters.date_to }),
    ...((filters.date_from || filters.date_to) && { tz: VIEWER_TIME_ZONE }),
  };
}

/**
 * Admin > Audit Logs: what happened in the organization, who did it and when.
 * Fakto's own work inside the organization shows as one actor, Fakto
 * Support; the API decides what is shown (lambda/app-api/audit-allowlist.ts).
 */
export function AuditLogsTab() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZES[0], total: 0, totalPages: 0 });

  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [sort, setSort] = useState<{ by: AuditLogSortColumn; dir: SortDir }>({ by: 'time', dir: 'desc' });
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [options, setOptions] = useState<AuditLogFilterOptions>({ events: [], users: [], projects: [] });

  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const closeDetails = useCallback(() => setSelectedLog(null), []);
  const [showExport, setShowExport] = useState(false);

  useEffect(() => {
    api
      .getAuditLogFilters()
      .then(setOptions)
      .catch((err) => console.error('Failed to load audit log filters:', err));
  }, []);

  const apiFilters = useMemo(() => toApiFilters(filters), [filters]);

  // Only the latest request may fill the table (as in the admin console's
  // AuditLogList): a slower response to an earlier query would otherwise land
  // last and show rows, and an export count, the applied filters exclude
  const latestRequest = useRef(0);

  const loadLogs = useCallback(
    async (page = 1) => {
      const request = ++latestRequest.current;
      setLoading(true);
      try {
        const result = await api.listAuditLogs({
          ...apiFilters,
          page,
          limit: pageSize,
          sort_by: sort.by,
          sort_order: sort.dir,
        });
        if (request !== latestRequest.current) return;
        setLogs(result.logs);
        setPagination(result.pagination);
        setError('');
      } catch (err) {
        if (request !== latestRequest.current) return;
        setError(getErrorMessage(err));
      } finally {
        if (request === latestRequest.current) setLoading(false);
      }
    },
    [apiFilters, sort, pageSize]
  );

  // Initial load, and back to page 1 on a filter, sort or page size change
  useEffect(() => {
    loadLogs(1);
  }, [loadLogs]);

  const sortProps = (column: AuditLogSortColumn) => ({
    sortable: true,
    sortActive: sort.by === column,
    sortDir: sort.dir,
    onSort: (dir: SortDir) => setSort({ by: column, dir }),
  });

  const userOptions = [
    { value: FAKTO_SUPPORT_FILTER, label: FAKTO_SUPPORT_LABEL },
    ...options.users.map((u) => ({ value: u.id, label: u.email })),
  ];
  const eventOptions = options.events.map((e) => ({ value: e.event, label: eventLabel(e.event) }));
  const projectOptions = [
    { value: NO_PROJECT_FILTER, label: 'No project' },
    ...options.projects.map((p) => ({ value: p.id, label: p.name })),
  ];

  // What the export's "Selected data" means, in words. Paging and sorting are not filters.
  const filterSummary = (() => {
    const names = (values: string[], opts: { value: string; label: string }[]) =>
      values.map((v) => opts.find((o) => o.value === v)?.label ?? v).join(', ');
    const lines: string[] = [];
    if (filters.date_from || filters.date_to) {
      lines.push(`Time: ${filters.date_from?.replace('T', ' ') ?? '…'} → ${filters.date_to?.replace('T', ' ') ?? '…'}`);
    }
    if (filters.user_ids.length > 0) lines.push(`User: ${names(filters.user_ids, userOptions)}`);
    if (filters.events.length > 0) lines.push(`Action: ${names(filters.events, eventOptions)}`);
    if (filters.project_ids.length > 0) lines.push(`Project: ${names(filters.project_ids, projectOptions)}`);
    if (filters.search) lines.push(`Item contains “${filters.search}”`);
    if (filters.exclude_fakto) lines.push('Fakto Support activity excluded');
    return lines;
  })();
  const hasActiveFilters = filterSummary.length > 0;

  return (
    <div className="tab-content audit-logs-tab">
      <div className="audit-toolbar">
        <label className="audit-exclude-fakto">
          <input
            type="checkbox"
            checked={filters.exclude_fakto}
            onChange={(e) => setFilters({ ...filters, exclude_fakto: e.target.checked })}
          />
          Exclude {FAKTO_SUPPORT_LABEL} activity
        </label>
        <button className="btn-primary" onClick={() => setShowExport(true)}>
          Export
        </button>
      </div>

      <div className="results-bar">
        <div className="results-summary">
          Showing {logs.length} of {pagination.total.toLocaleString('en-US')} events
          {hasActiveFilters && (
            <>
              {' (filtered) · '}
              <button className="btn-link" onClick={() => setFilters(defaultFilters)}>
                Clear all filters
              </button>
            </>
          )}
        </div>
        <label className="page-size">
          Rows per page
          <select className="status-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <div className="error-message">{error}</div>}

      <table className="data-table audit-log-table">
        <thead>
          <tr>
            <th>
              <DateRangeFilterPopover
                label="Time"
                timeZone={VIEWER_TIME_ZONE}
                allowTime
                {...sortProps('time')}
                value={{ from: filters.date_from, to: filters.date_to }}
                onChange={(v) => setFilters({ ...filters, date_from: v.from, date_to: v.to })}
              />
            </th>
            <th>
              <MultiSelectFilterPopover
                label="User"
                {...sortProps('user')}
                value={filters.user_ids}
                options={userOptions}
                onChange={(v) => setFilters({ ...filters, user_ids: v })}
                searchable={userOptions.length > 10}
                searchPlaceholder="Search users…"
              />
            </th>
            <th>
              <MultiSelectFilterPopover
                label="Action"
                {...sortProps('action')}
                value={filters.events}
                options={eventOptions}
                onChange={(v) => setFilters({ ...filters, events: v })}
                searchable={eventOptions.length > 10}
                searchPlaceholder="Search actions…"
              />
            </th>
            <th>
              <MultiSelectFilterPopover
                label="Project"
                {...sortProps('project')}
                value={filters.project_ids}
                options={projectOptions}
                onChange={(v) => setFilters({ ...filters, project_ids: v })}
                searchable={projectOptions.length > 10}
                searchPlaceholder="Search projects…"
              />
            </th>
            <th>
              <TextFilterPopover
                label="Item"
                align="right"
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                placeholder="File, folder, user, app…"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} className="empty-state">
                Loading...
              </td>
            </tr>
          ) : (
            <>
              {logs.map((log) => (
                <tr
                  key={log.id}
                  className={`audit-log-row ${selectedLog?.id === log.id ? 'selected' : ''}`}
                  onClick={() => setSelectedLog(log)}
                >
                  <td className="audit-time">{formatDateTime(log.created_at)}</td>
                  <td>
                    <AuditActor log={log} />
                  </td>
                  <td>{eventLabel(log.event)}</td>
                  <td>
                    {log.project ? (
                      <span className={log.project.name ? undefined : 'audit-muted'}>{projectLabel(log.project)}</span>
                    ) : (
                      <span className="audit-muted">-</span>
                    )}
                  </td>
                  <td className="audit-item" title={summarize(log)}>
                    {summarize(log)}
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={5} className="empty-state">
                    {hasActiveFilters ? 'No events match your filters' : 'No events yet'}
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>

      {!loading && pagination.totalPages > 1 && (
        <div className="pagination">
          <button className="btn-secondary" disabled={pagination.page <= 1} onClick={() => loadLogs(pagination.page - 1)}>
            Previous
          </button>
          <span className="page-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="btn-secondary"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => loadLogs(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {selectedLog && <AuditLogDetailsPanel log={selectedLog} onClose={closeDetails} />}

      {showExport && (
        <ExportAuditLogsModal
          filters={apiFilters}
          filterSummary={filterSummary}
          selectionCount={pagination.total}
          timeZone={VIEWER_TIME_ZONE}
          onClose={() => setShowExport(false)}
          onExported={() => {
            setShowExport(false);
            // The export is itself an event: show it
            loadLogs(1);
          }}
        />
      )}
    </div>
  );
}
